import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import {
  getEditorHostSx,
  withCollapsibleRichTextDefaults,
} from 'components/inspection/CollapsibleInspectionComment/model';
import {
  commentRootSx,
  floatingLabelSx,
  formatToolbarToggleSlotSx,
  formatToolbarToggleSx,
} from 'components/inspection/CollapsibleInspectionComment/styles';
import {
  hasRichTextValue,
  isCompactSingleLineLayout,
  isPrintableKey,
  scheduleNestedAnimationFrames,
} from './model';
import type { CollapsibleCommentShellProps } from './types';
import { isInsideRichTextPopup } from 'components/common/RichTextEditor/richTextPopup';

export function CollapsibleCommentShell({
  value,
  label,
  locked,
  children,
  focusSignal = 0,
  labelDataQa,
  toggleDataQa,
  showFloatingLabel,
}: CollapsibleCommentShellProps): ReactElement {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);
  const [formatToolbarOpen, setFormatToolbarOpen] = useState(false);
  const editorHostRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef(false);
  const cancelScheduledFocusRef = useRef<(() => void) | null>(null);
  const hasText = hasRichTextValue(value);
  const compactSingleLineLayout = isCompactSingleLineLayout(value);

  useEffect(() => {
    if (hasText) {
      setExpanded(true);
    }
  }, [hasText]);

  const focusEditor = useCallback((): boolean => {
    const editable = editorHostRef.current?.querySelector<HTMLElement>(
      '[contenteditable="true"]'
    );
    if (!editable) {
      return false;
    }
    editable.focus({ preventScroll: true });
    return (
      document.activeElement === editable ||
      editable.contains(document.activeElement)
    );
  }, []);

  const scheduleFocusEditor = useCallback((): void => {
    cancelScheduledFocusRef.current?.();
    cancelScheduledFocusRef.current = scheduleNestedAnimationFrames(() => {
      if (!focusEditor()) {
        return false;
      }
      pendingFocusRef.current = false;
      return true;
    }, 2);
  }, [focusEditor]);

  useEffect(() => {
    return () => {
      cancelScheduledFocusRef.current?.();
      cancelScheduledFocusRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    if (!pendingFocusRef.current || !expanded) {
      return;
    }
    scheduleFocusEditor();
  }, [expanded, scheduleFocusEditor]);

  useLayoutEffect(() => {
    if (!focusSignal) {
      return;
    }
    setExpanded(true);
    pendingFocusRef.current = true;
    scheduleFocusEditor();
  }, [focusSignal, scheduleFocusEditor]);

  const handleFocusCapture = (): void => {
    setFocused(true);
    pendingFocusRef.current = false;
    if (!locked) {
      setExpanded(true);
    }
  };

  const handleToggleFormatToolbar = (): void => {
    if (locked) {
      return;
    }
    setExpanded(true);
    setFormatToolbarOpen((open) => !open);
    scheduleFocusEditor();
  };

  const handleMouseDownCapture = (event: MouseEvent<HTMLElement>): void => {
    if (locked || !editorHostRef.current?.contains(event.target as Node)) {
      return;
    }
    if (
      (event.target as HTMLElement).closest(
        '[data-format-toolbar-toggle="true"]'
      )
    ) {
      return;
    }

    const editable = editorHostRef.current.querySelector<HTMLElement>(
      '[contenteditable="true"]'
    );
    const editableHasFocus =
      editable != null &&
      (document.activeElement === editable ||
        editable.contains(document.activeElement));

    setExpanded(true);

    if (editableHasFocus) {
      return;
    }

    event.preventDefault();
    pendingFocusRef.current = true;
    scheduleFocusEditor();
  };

  const floatingLabelShrunk = focused || hasText || expanded;

  const handleBlur = (event: FocusEvent<HTMLElement>): void => {
    const nextTarget = event.relatedTarget;
    // A popup of the editor (the colour picker) is rendered outside the field, but belongs to it.
    if ((nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) || isInsideRichTextPopup(nextTarget)) {
      return;
    }
    setFocused(false);
    setFormatToolbarOpen(false);
    if (expanded && !hasText) {
      setExpanded(false);
    }
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLElement>): void => {
    if (expanded || locked) {
      return;
    }
    if (isPrintableKey(event)) {
      setExpanded(true);
    }
  };

  const handlePasteCapture = (_event: ClipboardEvent<HTMLElement>): void => {
    if (expanded || locked) {
      return;
    }
    setExpanded(true);
  };

  const renderedChild = useMemo(
    () => withCollapsibleRichTextDefaults(children),
    [children]
  );

  const editorHostCombinedSx = useMemo(
    () => getEditorHostSx(compactSingleLineLayout, formatToolbarOpen),
    [compactSingleLineLayout, formatToolbarOpen]
  );

  const formatToolbarTitle = formatToolbarOpen
    ? formatTranslation(intl, InspectionTranslationKeys.commentToolbarHide)
    : formatTranslation(intl, InspectionTranslationKeys.commentToolbarShow);

  return (
    <Box
      sx={commentRootSx}
      onFocusCapture={handleFocusCapture}
      onMouseDownCapture={handleMouseDownCapture}
      onBlur={handleBlur}
      onKeyDownCapture={handleKeyDownCapture}
      onPasteCapture={handlePasteCapture}
    >
      <Box
        ref={editorHostRef}
        data-format-toolbar-open={formatToolbarOpen ? 'true' : undefined}
        sx={editorHostCombinedSx}
      >
        {renderedChild}
        {!locked ? (
          <Box
            sx={formatToolbarToggleSlotSx(formatToolbarOpen)}
            data-format-toolbar-toggle='true'
          >
            <Tooltip title={formatToolbarTitle}>
              <IconButton
                size='small'
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={handleToggleFormatToolbar}
                data-qa={toggleDataQa}
                sx={formatToolbarToggleSx}
                color={formatToolbarOpen ? 'primary' : 'default'}
              >
                <TextFieldsIcon sx={{ fontSize: '1rem' }} />
                {formatToolbarOpen ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: '1.1rem' }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: '1.1rem' }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
      {showFloatingLabel ? (
        <Typography
          component='label'
          sx={floatingLabelSx(floatingLabelShrunk, focused)}
          data-qa={labelDataQa}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}
